import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

import Pagination from './Pagination.js';
import SelectGuide from './SelectGuide.js';
import DirectoryTable from './DirectoryTable.js';
import { file_path } from './environment';
import { filterItems } from './listUtils';

export default function Lawyers() {
    const [guides, setGuides] = useState([]);
    const [guide, setGuide] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [allItems, setAllItems] = useState([]);
    const [years, setYears] = useState([]);
    const [items, setItems] = useState([]);
    const [filters, setFilters] = useState({});
    const latestRequestId = useRef(0);
    const navigate = useNavigate();
    const { guideId } = useParams();

    useEffect(() => {
        document.title = guide?.name ? `${guide.name} Lawyers` : 'Lawyers';
    }, [guide?.name]);

    function handlePage(newPage) {
        setPage(newPage);
    }

    function handleChangePageSize(event) {
        const newPageSize = event.target.value;
        setPageSize(newPageSize);

        const lastPage = Math.ceil(items.length / newPageSize);
        if (page > lastPage) {
            setPage(lastPage);
        }
    }

    const guideSelected = useCallback((newGuide, options = {}) => {
        const { replace = false } = options;

        if (!newGuide) {
            return;
        }

        setGuide(newGuide);
        setFilters({});
        setPage(1);
        setAllItems([]);
        setItems([]);
        setYears([]);

        const nextGuideId = newGuide.id.toString();

        if (guideId?.toString() !== nextGuideId) {
            navigate(`/lawyers/${nextGuideId}`, { replace });
        }

        const requestId = latestRequestId.current + 1;
        latestRequestId.current = requestId;

        axios.get(file_path(`/lawyers/${newGuide.id}.json`)).then((res) => {
            if (requestId !== latestRequestId.current) {
                return;
            }

            const nextItems = Array.isArray(res.data.lawyers) ? res.data.lawyers : [];
            const nextYears = res.data.years || {};

            setAllItems(nextItems);
            setItems(nextItems);
            setYears(nextYears);
        }).catch(() => {
            if (requestId !== latestRequestId.current) {
                return;
            }

            setAllItems([]);
            setItems([]);
            setYears([]);
        });
    }, [guideId, navigate]);

    function handleFilterChange(event) {
        const filterId = event.target.id;
        const filterValue = event.target.value;
        const nextFilters = { ...filters };

        if (!filterValue) {
            delete nextFilters[filterId];
        } else {
            nextFilters[filterId] = filterValue;
        }

        setFilters(nextFilters);
        setItems(filterItems(allItems, nextFilters));
        setPage(1);
    }

    function findGuideById(guideList, guideId = 5) {
        return guideList.find((element) => element.id.toString() === guideId.toString());
    }

    useEffect(() => {
        axios.get(file_path('/guides.json')).then((res) => {
            setGuides(res.data.guides);

            const found = findGuideById(res.data.guides, guideId);
            if (found) {
                guideSelected(found, { replace: true });
            }
        });
    }, [guideId, guideSelected]);

    return (
        <div id="directory">
            <nav>
                <Link to="/guides" role="button">Return to Portal</Link>
                <Link to={guide?.id ? `/firms/${guide.id}` : '/firms'} role="button">Firms</Link>
                <Link to={guide?.id ? `/lawyers/${guide.id}` : '/lawyers'} role="button">Lawyers</Link>
            </nav>
            <h1>{guide?.name ? `${guide.name} Lawyers` : 'Lawyers'}</h1>
            <SelectGuide guides={guides} value={guide} guideSelected={guideSelected} />
            <div className="navigation">
                <Pagination page={page} pageSize={pageSize} items={items} onPage={handlePage} onChangePageSizeChange={handleChangePageSize} />
            </div>
            <div className="table">
                <DirectoryTable
                    items={items}
                    years={years}
                    page={page}
                    pageSize={pageSize}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    columns={[
                        { key: 'name', label: 'Name', field: 'name', filterable: true },
                        { key: 'firm', label: 'Firm', field: 'firm', filterable: true },
                        { key: 'location', label: 'Location', field: 'location', filterable: true },
                        { key: 'practice', label: 'Practice', field: 'practice', filterable: true },
                    ]}
                />
            </div>
            <div className="navigation">
                <Pagination page={page} pageSize={pageSize} items={items} onPage={handlePage} onChangePageSizeChange={handleChangePageSize} />
            </div>
        </div>
    );
}
